import type {
  DocumentNode,
  MutationHookOptions,
  OperationVariables,
} from '@apollo/client';
import { useMutation } from '@apollo/client';
import { useSnackbar } from 'hephaestus/web';

export type ToastFn = (opts: {
  severity: 'success' | 'error' | 'warning' | 'info';
  message: string;
}) => void;

interface TradingMutationOptions<TData, TVariables extends OperationVariables>
  extends MutationHookOptions<TData, TVariables> {
  successMessage?: string;
  toast?: ToastFn;
}

/**
 * Wraps Apollo's useMutation with standardized toast-based
 * error/success feedback. Eliminates duplicated try/catch/alert patterns.
 */
export const useTradingMutation = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  mutation: DocumentNode,
  options?: TradingMutationOptions<TData, TVariables>,
) => {
  const { enqueueSnackbar } = useSnackbar();

  const { successMessage, toast, ...apolloOptions } = options ?? {};
  const defaultToast: ToastFn = ({ message, severity }) =>
    enqueueSnackbar(message, { variant: severity });
  const notify: ToastFn = toast ?? defaultToast;

  return useMutation<TData, TVariables>(mutation, {
    ...apolloOptions,
    onError: (err, clientOptions) => {
      notify({
        severity: 'error',
        message: err?.message || 'An unexpected error occurred',
      });
      apolloOptions?.onError?.(err, clientOptions);
    },
    onCompleted: (data, clientOptions) => {
      if (successMessage) {
        notify({ severity: 'success', message: successMessage });
      }
      apolloOptions?.onCompleted?.(data, clientOptions);
    },
  });
};
